import { useEffect } from 'react'
import { getLocaleUrls } from '../lib/localeUrls.js'

const ARTICLE_URL = 'https://www.guidebg.com/in-the-measures-of-mankind/'
const GUIDE_BG_URL = 'https://www.guidebg.com/'

const seoCopy = {
  en: {
    title: 'Measures of Mankind | Unit Converter & Bulgarian Measures',
    description: 'Convert 203 modern and historical units across 18 categories. Explore SI, Imperial, scientific and traditional measures used on Bulgarian lands.',
    imageAlt: 'Illustration of people using rulers, scales and geometric measuring tools',
    applicationCategory: 'Unit converter and measurement reference',
    featureList: [
      '203 modern and historical measurement units',
      '18 conversion categories',
      'SI, Imperial, US customary and scientific units',
      'Historical measures used on Bulgarian lands',
      'English, Bulgarian and German interface',
    ],
  },
  bg: {
    title: 'Measures of Mankind | Конвертор на мерни единици',
    description: 'Преобразувайте 203 съвременни и исторически единици в 18 категории. Открийте SI, имперски, научни и стари мерки от българските земи.',
    imageAlt: 'Илюстрация на хора с линийки, везни и геометрични измервателни инструменти',
    applicationCategory: 'Конвертор и справочник за мерни единици',
    featureList: [
      '203 съвременни и исторически мерни единици',
      '18 категории за преобразуване',
      'SI, имперски, американски и научни единици',
      'Исторически мерки, използвани по българските земи',
      'Интерфейс на английски, български и немски език',
    ],
  },
  de: {
    title: 'Measures of Mankind | Maßeinheiten-Umrechner',
    description: '203 moderne und historische Einheiten in 18 Kategorien umrechnen. SI-, Imperial-, wissenschaftliche und historische Maße aus Bulgarien entdecken.',
    imageAlt: 'Illustration von Menschen mit Linealen, Waagen und geometrischen Messwerkzeugen',
    applicationCategory: 'Maßeinheiten-Umrechner und Nachschlagewerk',
    featureList: [
      '203 moderne und historische Maßeinheiten',
      '18 Umrechnungskategorien',
      'SI-, Imperial-, US- und wissenschaftliche Einheiten',
      'Historische Maße auf bulgarischem Gebiet',
      'Englische, bulgarische und deutsche Benutzeroberfläche',
    ],
  },
}

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.append(element)
  }
  element.setAttribute('content', content)
}

function upsertLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement('link')
    element.rel = rel
    if (hreflang) element.hreflang = hreflang
    document.head.append(element)
  }
  element.href = href
}

function setAlternateLocales(locales) {
  document.head.querySelectorAll('meta[property="og:locale:alternate"]').forEach((element) => element.remove())
  locales.forEach((content) => {
    const element = document.createElement('meta')
    element.setAttribute('property', 'og:locale:alternate')
    element.setAttribute('content', content)
    document.head.append(element)
  })
}

export default function SeoHead({ locale }) {
  useEffect(() => {
    const copy = seoCopy[locale] ?? seoCopy.en
    const urls = getLocaleUrls()
    const language = locale === 'bg' ? 'bg-BG' : locale === 'de' ? 'de-DE' : 'en'
    const ogLocale = locale === 'bg' ? 'bg_BG' : locale === 'de' ? 'de_DE' : 'en_US'
    const currentUrl = urls[locale] ?? urls.en

    document.documentElement.lang = locale
    document.title = copy.title

    upsertMeta('name', 'description', copy.description)
    upsertMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1')
    upsertMeta('name', 'author', 'GuideBG')
    upsertMeta('name', 'application-name', 'Measures of Mankind')

    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:site_name', 'GuideBG')
    upsertMeta('property', 'og:title', copy.title)
    upsertMeta('property', 'og:description', copy.description)
    upsertMeta('property', 'og:url', currentUrl)
    upsertMeta('property', 'og:locale', ogLocale)
    setAlternateLocales(['en_US', 'bg_BG', 'de_DE'].filter((item) => item !== ogLocale))
    upsertMeta('property', 'og:image', urls.image)
    upsertMeta('property', 'og:image:width', '1536')
    upsertMeta('property', 'og:image:height', '1024')
    upsertMeta('property', 'og:image:alt', copy.imageAlt)

    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', copy.title)
    upsertMeta('name', 'twitter:description', copy.description)
    upsertMeta('name', 'twitter:image', urls.image)
    upsertMeta('name', 'twitter:image:alt', copy.imageAlt)

    upsertLink('canonical', currentUrl)
    upsertLink('alternate', urls.en, 'en')
    upsertLink('alternate', urls.bg, 'bg')
    upsertLink('alternate', urls.de, 'de')
    upsertLink('alternate', urls.base, 'x-default')

    let structuredData = document.getElementById('measures-structured-data')
    if (!structuredData) {
      structuredData = document.createElement('script')
      structuredData.id = 'measures-structured-data'
      structuredData.type = 'application/ld+json'
      document.head.append(structuredData)
    }
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${GUIDE_BG_URL}#website`,
          url: GUIDE_BG_URL,
          name: 'GuideBG',
          inLanguage: ['en', 'bg', 'de'],
        },
        {
          '@type': 'WebPage',
          '@id': `${currentUrl}#webpage`,
          url: currentUrl,
          name: copy.title,
          description: copy.description,
          inLanguage: language,
          isPartOf: { '@id': `${GUIDE_BG_URL}#website` },
          mainEntity: { '@id': `${urls.base}#converter` },
          image: urls.image,
          subjectOf: { '@type': 'Article', url: ARTICLE_URL, name: 'In the Measures of Mankind' },
        },
        {
          '@type': 'WebApplication',
          '@id': `${urls.base}#converter`,
          url: currentUrl,
          name: 'Measures of Mankind',
          description: copy.description,
          applicationCategory: 'UtilitiesApplication',
          applicationSubCategory: copy.applicationCategory,
          operatingSystem: 'Any',
          browserRequirements: 'Requires JavaScript',
          isAccessibleForFree: true,
          inLanguage: ['en', 'bg', 'de'],
          image: urls.image,
          screenshot: urls.image,
          featureList: copy.featureList,
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
          creator: { '@type': 'Organization', name: 'GuideBG', url: GUIDE_BG_URL },
        },
      ],
    })
  }, [locale])

  return null
}
